/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/services/api/bhxhComplianceService.ts
 * PURPOSE: BHXH Compliance API Service - EPIC 10.2 HR Portal. Paths follow the
 *          mounted backend router /api/v1/bhxh/* (audit A2, 2026-10-05).
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Sprint Governance - Rule 7: API Contract)
 * SPRINT GOVERNANCE: Rule 7: Performance SLAs (<50ms p95)
 *
 * CTO MANDATE: Automated BHXH calculation with monthly submission report generation
 *
 * @ai-human-orchestration
 * - AIRole: Senior Frontend Developer + Compliance Engineer
 * - HumanHandoff: Local Team validates Vietnamese compliance rules
 * - CollaborationLevel: Remote Team implements, HR Officer validates
 *
 * @business-context
 * - Domain: Vietnamese F&B BHXH Compliance
 * - Impact: Compliance Critical - ₫35.1.3B+ penalty risk exposure
 * - Stakeholders: NQH HR Officer, CFO, Vietnamese Government (BHXH)
 *
 * @technical-architecture
 * - Layer: Data Access Layer, API Services
 * - Pattern: Service Pattern, Axios Client, Type-Safe API
 * - Dependencies: Axios, TypeScript, Backend BHXH APIs
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/
 * - API: docs/03-integrate/01-API-Specifications/
 *  *
 * Sprint 10 EPIC 10.2: BHXH Compliance Automation (6 SP)
 * - Automated BHXH calculation (21.5% rate, 46.8M ceiling)
 * - Monthly submission report generation
 * - Labor Law 2019 compliance validation
 * - ₫35.1.3B+ penalty risk dashboard
 */

import { apiClient } from './client';

// ============= BHXH Constants (2025) =============

export const BHXH_RATES_2025 = {
  employer: 0.175, // 17.5%
  employee: 0.08, // 8%
  total: 0.215, // 21.5%
  ceiling: 46800000, // 46.8M VND
  minWageRegion1: 4960000, // 4.96M VND
} as const;

// ============= Type Definitions =============

export interface BHXHContribution {
  employee_id: string;
  employee_name: string;
  bhxh_number: string;
  base_salary: number;
  contribution_base: number;
  ceiling_applied: boolean;
  employer_contribution: number;
  employee_contribution: number;
  total_contribution: number;
  month: string;
}

export interface BHXHSubmissionReport {
  submission_month: string;
  company_info: {
    name: string;
    tax_code: string;
  };
  summary: {
    total_employees: number;
    total_employer_contribution: number;
    total_employee_contribution: number;
    total_combined_contribution: number;
  };
  rates: {
    employer_rate: string;
    employee_rate: string;
    combined_rate: string;
    ceiling: number;
  };
  employees: BHXHContribution[];
  export_formats: string[];
  generated_at: string;
  // DD-0067: "PENDING_REAL_DATA" while salary data is not wired (backend/app/compliance/bhxh_automation.py)
  data_status?: string;
  data_status_message_vi?: string;
}

export const BHXH_PENDING_REAL_DATA = 'PENDING_REAL_DATA';

export interface ComplianceViolation {
  violation_type: string;
  employee_id: string | null;
  employee_name: string | null;
  description: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  potential_penalty: number;
  detected_at: string;
  resolved_at: string | null;
}

export interface PenaltyRiskAssessment {
  assessment_date: string;
  risk_breakdown: {
    bhxh_risk: number;
    labor_law_risk: number;
    tax_risk: number;
    total_risk: number;
  };
  violations_count: number;
  violations: ComplianceViolation[];
}

export interface PenaltyRiskResponse {
  assessment: PenaltyRiskAssessment;
  compliance_status: 'compliant' | 'at_risk';
  risk_level: 'low' | 'medium' | 'high' | 'critical';
  recommended_actions: {
    priority: string;
    action: string;
    deadline: string;
  }[];
}

export interface LaborLawPolicy {
  id: string;
  name: string;
  description: string;
  status: 'pass' | 'fail';
  violations: number;
}

export interface LaborLawValidationResponse {
  compliance_date: string;
  policies_checked: number;
  policies_passed: number;
  compliance_rate: string;
  overall_status: 'compliant' | 'at_risk';
  policies: LaborLawPolicy[];
  vietnamese_labor_law_version: string;
  data_status?: string;
  data_status_message_vi?: string;
}

// ============= BHXH Compliance API Service =============

export const bhxhComplianceService = {
  /**
   * Get monthly BHXH submission report
   * API: GET /api/v1/bhxh/submission-report
   *
   * @param submissionMonth Month in YYYY-MM format
   */
  getSubmissionReport: async (
    submissionMonth?: string
  ): Promise<BHXHSubmissionReport> => {
    const params = new URLSearchParams();
    if (submissionMonth) params.append('submission_month', submissionMonth);

    const response = await apiClient.get<BHXHSubmissionReport>(
      `/bhxh/submission-report?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Get penalty risk assessment dashboard
   * API: GET /api/v1/bhxh/penalty-risk
   *
   * Returns ₫35.1.3B+ penalty exposure analysis including:
   * - BHXH late submission penalties
   * - Labor Law violation fines
   * - Tax compliance risks
   */
  getPenaltyRisk: async (): Promise<PenaltyRiskResponse> => {
    const response = await apiClient.get<PenaltyRiskResponse>(
      '/bhxh/penalty-risk'
    );
    return response.data;
  },

  /**
   * Validate Labor Law 2019 compliance
   * API: GET /api/v1/bhxh/compliance-checklist (no validate-labor-law route exists)
   *
   * Checks 8 key policies:
   * 1. OT limits validation
   * 2. Rest period validation (≥12h)
   * 3. Night shift premium validation
   * 4. BHXH registration validation
   * 5. Working hours limit validation
   * 6. Probation period validation
   * 7. Contract compliance validation
   * 8. Minimum wage validation
   */
  validateLaborLaw: async (): Promise<LaborLawValidationResponse> => {
    const response = await apiClient.get<LaborLawValidationResponse>(
      '/bhxh/compliance-checklist'
    );
    return response.data;
  },
};

// ============= Utility Functions =============

/**
 * Format VND currency for Vietnamese display
 */
export const formatVND = (amount: number): string => {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
    maximumFractionDigits: 0,
  }).format(amount);
};

/**
 * Get compliance status badge colors and text
 */
export const getComplianceStatusBadge = (status: 'compliant' | 'at_risk'): {
  color: string;
  bgColor: string;
  text: string;
} => {
  if (status === 'compliant') {
    return { color: '#22c55e', bgColor: 'rgba(34, 197, 94, 0.1)', text: 'Tuân thủ' };
  }
  return { color: '#ef4444', bgColor: 'rgba(239, 68, 68, 0.1)', text: 'Cần xem xét' };
};

/**
 * Get risk level badge colors and text
 */
export const getRiskLevelBadge = (level: 'low' | 'medium' | 'high' | 'critical'): {
  color: string;
  bgColor: string;
  text: string;
} => {
  switch (level) {
    case 'low':
      return { color: '#22c55e', bgColor: 'rgba(34, 197, 94, 0.1)', text: 'Thấp' };
    case 'medium':
      return { color: '#eab308', bgColor: 'rgba(234, 179, 8, 0.1)', text: 'Trung bình' };
    case 'high':
      return { color: '#f97316', bgColor: 'rgba(249, 115, 22, 0.1)', text: 'Cao' };
    case 'critical':
      return { color: '#ef4444', bgColor: 'rgba(239, 68, 68, 0.1)', text: 'Nghiêm trọng' };
    default:
      return { color: '#6b7280', bgColor: 'rgba(107, 114, 128, 0.1)', text: 'Không xác định' };
  }
};

/**
 * Get policy status icon and color
 */
export const getPolicyStatusDisplay = (status: 'pass' | 'fail'): {
  icon: string;
  color: string;
  text: string;
} => {
  if (status === 'pass') {
    return { icon: '✓', color: '#22c55e', text: 'Đạt' };
  }
  return { icon: '✗', color: '#ef4444', text: 'Không đạt' };
};

/**
 * Format month for Vietnamese display
 */
export const formatVietnameseMonth = (monthStr: string): string => {
  const [year, month] = monthStr.split('-');
  return `Tháng ${month}/${year}`;
};

export default bhxhComplianceService;
